import moment, { Moment } from 'moment'

import { ConfigItem, DefaultFile, MultipleDefaultFile } from 'Core/interfaces'

import { ContactFormData } from 'Portals/Internal/Components/ContactListForm/ContactListForm'
import { ServiceLocationStatus } from 'Portals/Internal/constants'
import { Contact as DefaultContact } from 'Portals/Internal/entities/Contact'

import { BillingLocation } from './BillingLocation'

export enum SLAddAddressFormLabels {
	Latitude = 'latitude',
	Longitude = 'longitude',
	LocalityId1 = 'localityId1',
	LocalityId2 = 'localityId2',
	LocalityId3 = 'localityId3'
}

export enum SupplierUseLabels {
	SupplierLocationId = 'supplierLocationId',
	SupplierAccountNumber = 'supplierAccountNumber',
	OrderNotification = 'orderNotification',
	AutoAcceptOrder = 'autoAcceptOrder',
	IsActive = 'isActive'
}

export enum SLSiteBuildingInfoFormLabels {
	buildingName = 'buildingName',
	squareFootage = 'squareFootage',
	notes = 'notes',
	contacts = 'contacts',
	buildingTypeId = 'buildingTypeId'
}

export enum SLDocumentsFormLabels {
	documentName = 'documentName',
	documentTypeId = 'documentTypeId',
	documentType = 'documentType',
	fileName = 'fileName',
	allowCustomerToSee = 'allowCustomerToSee',
	file = 'file'
}

export enum CurrentServiceProductStateFields {
	id = 'id',
	name = 'name',
	annualVolume = 'annualVolume',
	unit = 'unit',
	annualHaulsOrServices = 'annualHaulsOrServices',
	disposalMethod = 'disposalMethod',
	currentVendor = 'currentVendor',
	contactExpiration = 'contactExpiration',
	containerType = 'containerType',
	containerSize = 'containerSize'
}

export enum CurrentServiceProductStateFormFields {
	name = 'name',
	currentVendor = 'currentVendor',
	containerType = 'containerType',
	containerSize = 'containerSize',
	annualVolume = 'annualVolume',
	annualHaulsOrServices = 'annualHaulsOrServices',
	unitOfMeasureId = 'unitOfMeasureId',
	treatmentMethodId = 'treatmentMethodId',
	contactExpiration = 'contactExpiration'
}

export enum CurrentSupplierContactFields {
	id = 'id',
	currentVendor = 'currentVendor',
	vendorContactName = 'vendorContactName',
	vendorContactEmail = 'vendorContactEmail',
	vendorContactPhone = 'vendorContactPhone',
	wastesMaterialsServices = 'wastesMaterialsServices'
}

export enum CurrentSupplierContactFormFields {
	currentVendor = 'currentVendor',
	vendorContactName = 'vendorContactName',
	vendorContactEmail = 'vendorContactEmail',
	vendorContactPhone = 'vendorContactPhone',
	existingPaymentTerms = 'existingPaymentTerms',
	wastesMaterialsServices = 'wastesMaterialsServices',
	isUnderContact = 'isUnderContact'
}

export enum HistoricalInvoiceFields {
	id = 'id',
	documentName = 'documentName',
	uploadDate = 'uploadDate'
}

export enum HistoricalInvoiceFormFields {
	files = 'files'
}

export interface SlContactFlags {
	isMainContact: boolean
	isInvoiceContact: boolean
	isManifestContact: boolean
	isOrderingContact: boolean
	isSurvey: boolean
	isCollectionContact: boolean
	isOffspecContact: boolean
}

export interface SLSiteBuildingInfoContacts {
	firstName: string
	lastName: string
	email: ContactFormData['email']
	id: ContactFormData['id']
	contactId: ContactFormData['contactId']
	isActive: ContactFormData['isActive']
}

export interface SLSiteBuildingInfoFormContacts {
	id: ContactFormData['id']
	isActive: ContactFormData['isActive']
	contact: {
		key: ContactFormData['contactId']
		value: ContactFormData['contactId']
		label: string
	} | null
}

export interface NoteListItem {
	[key: string]: string
}

export interface NoteTypeDataItem {
	fieldName: keyof ServiceLocation.Notes.Basic
	label: string
	maxLength: number
}

export enum ServiceLocationSearchResultFields {
	searchResultById = 'searchResultById',
	searchResultByName = 'searchResultByName',
	searchResultByRefCode = 'searchResultByRefCode',
	searchResultByCompanyId = 'searchResultByCompanyId',
	searchResultByCompanyName = 'searchResultByCompanyName',
	searchResultBySupplierServiceSiteCode = 'searchResultBySupplierServiceSiteCode'
}

export enum ServiceLocationFilterFields {
	statusId = 'StatusId'
}

export enum SLAddressSearchResultFields {
	stateResponse = 'states',
	cityResponse = 'cities',
	addressResponse = 'addresses'
}

export enum SLProgramBasicFields {
	id = 'id',
	salesforceId = 'salesforceId',
	programName = 'programName',
	programDescription = 'programDescription',
	isActive = 'isActive'
}

export enum SLProgramGenericFormFields {
	salesforceId = 'salesforceId',
	programName = 'programName',
	description = 'description',
	isActive = 'isActive'
}

export type Location = {
	city: string | null
	province: Entity | null
	address: string | null
}

export declare namespace ServiceLocation {
	interface Basic extends Entity {
		address: string | null
		status: ServiceLocationStatus
		location: string | null
		company: Entity | null
		serviceTeam: string | null
		stateProvince: Entity | null
		city: BasicString
	}

	interface InvoiceLoaderBasic extends Entity {
		location: Location
		company: Entity | null
	}

	interface AdditionalFields {
		epaId: string | null
		sicCode: Entity | null
		zwlfDate: string | null
		naicsCode: Entity | null
		zwlfAuditDate: string | null
		reportingStatus: Entity | null
		xServiceSiteRefCode: string | null
		parentHierarchyLevel: Entity | null
		advancedHazNotificationDays: number | null
		emergencyResponsePhoneNumber: string | null
		emergencyResponseSupplierLocation: Entity | null
	}

	namespace Form {
		interface Add {
			serviceLocationName: string
			serviceLocationNameInNativeLanguage: string
			customerId: LabelValue<number> | null
			companyId: LabelValue<number> | null
			serviceTeamId: Key | null
			statusId: LabelValue | null
			siteVisitFrequencyId: LabelValue | null
			bpmpProfileId: LabelValue | null
			isHesAccount: boolean
			collectionEmail: boolean
			allowOutsourcingBilling: boolean
			parentHierarchyLevelId: Key | null
			specialInstructions: string
		}
		interface Edit
			extends Omit<
					AdditionalFields,
					| 'reportingStatus'
					| 'parentHierarchyLevel'
					| 'emergencyResponseSupplierLocation'
					| 'naicsCode'
					| 'sicCode'
				>,
				Form.Add {
			parentHierarchyLevelId: Key | null
			reportingStatusId: string | null
			emergencyResponseSupplierLocation: Key | null
			statementComment: BasicString
			sicCodeId: LabelValue<number> | null
			naicsCodeId: LabelValue<number> | null
			skipManagerApproval: boolean
		}
	}

	namespace Request {
		interface Add {
			serviceLocationName: string | null
			serviceLocationNameInNativeLanguage: string | null
			customerId: Key
			companyId: Key
			serviceTeamId: Key
			statusId: Key
			siteVisitFrequencyId: Key
			bpmpProfileId: Key
			isHesAccount: boolean
			collectionEmail: boolean
			allowOutsourcingBilling: boolean
			parentHierarchyLevelId: Key
			specialInstructions: string | null
			skipManagerApproval: boolean
		}
		interface Edit {
			serviceLocationName: string | null
			serviceLocationNameInNativeLanguage: string | null
			customerId: Key | null
			companyId: Key | null
			serviceTeamId: Key | null
			statusId: Key | null
			siteVisitFrequencyId: Key | null
			bpmpProfileId: Key | null
			isHesAccount: boolean
			collectionEmail: boolean
			allowOutsourcingBilling: boolean
			parentHierarchyLevelId: Key | null
			specialInstructions: string | null
			reportingStatusId: Key | null
			naicsCodeId: BasicNumber
			sicCodeId: BasicNumber
			epaId: string | null
			xServiceSiteRefCode: string | null
			advancedHazNotificationDays: Key | null
			emergencyResponseSupplierLocationId: Key | null
			emergencyResponsePhoneNumber: string | null
			zwlfDate: string | null
			zwlfAuditDate: string | null
			statementComment: BasicString
			skipManagerApproval: boolean
		}
	}

	interface Detailed
		extends Entity,
			AdditionalFields,
			Omit<
				Request.Add,
				| 'serviceLocationName'
				| 'customerId'
				| 'companyId'
				| 'serviceTeamId'
				| 'statusId'
				| 'bpmpProfileId'
				| 'siteVisitFrequencyId'
				| 'parentHierarchyLevelId'
			> {
		status: Entity
		company: Company
		customer: Entity
		serviceTeam: Entity
		bpmpProfile: Entity
		collectionEmail: boolean
		rebateCheckNotes: string | null
		siteVisitFrequency: Entity
		nameInNativeLanguage: string | null
		isTurkish: boolean
		statementComment: BasicString
		skipManagerApproval: boolean
	}

	interface Company extends Entity {
		billingAgencies: Entity[]
	}

	interface AddressEntity<T = string> extends Entity<T> {
		hasPhysicalAddress: boolean
	}
	namespace Address {
		interface Basic {
			id: number
			addressTypeId: number
			addressType: string
			addressLine1: string
			addressLine2: string
			city: string
			stateProvinceId: number
			state: string
			zipCode: string
			contact: string
			contactId: number
			latitude: number
			longitude: number
			usTaxRate: string
			locality1: Entity | null
			locality2: Entity | null
			locality3: Entity | null
		}
		interface Form extends BillingLocation.Address.Form.Generic {
			[SLAddAddressFormLabels.Latitude]: Key | null
			[SLAddAddressFormLabels.Longitude]: Key | null
			[SLAddAddressFormLabels.LocalityId1]: Key | null
			[SLAddAddressFormLabels.LocalityId2]: Key | null
			[SLAddAddressFormLabels.LocalityId3]: Key | null
		}
		interface Request extends BillingLocation.Address.Request.Generic {
			latitude: Key | null
			longitude: Key | null
			localityId1: Key | null
			localityId2: Key | null
			localityId3: Key | null
		}
	}

	namespace SupplerUse {
		interface Basic {
			id: number
			supplierLocation: Entity
			supplierAccountNumber: string
			orderNotification: boolean
			autoAcceptOrder: boolean
			isActive: boolean
		}
		interface Form {
			supplierLocationId: LabelValue<number> | null
			supplierAccountNumber: string
			orderNotification: boolean
			autoAcceptOrder: boolean
			isActive: boolean
		}
		interface Request extends Omit<Form, 'supplierLocationId'> {
			supplierLocationId: number
		}
	}

	namespace Equipment {
		interface Basic {
			id: number
			equipmentName: string | null
			equipmentLocation: string | null
			equipmentDescription: string | null
			placementDate: string | null
			removalDate: string | null
		}
		interface Form {
			equipmentName: string | null
			equipmentLocation: string | null
			equipmentDescription: string | null
			placementDate: moment.Moment | null
			removalDate: moment.Moment | null
		}
		interface Request {
			equipmentName: string | null
			equipmentLocation: string | null
			equipmentDescription: string | null
			placementDate: string | null
			removalDate: string | null
		}
	}

	namespace BuildingInfo {
		interface Basic {
			buildingName: string
			buildingType: Entity
			squareFootage: number
			notes: string
		}
	}

	namespace SiteBuildingInfo {
		interface Basic {
			id: number
			buildingName: string
			buildingType: Entity
			squareFootage: number | null
			notes: string | null
		}
		interface Contact extends ServiceLocation.SiteBuildingInfo.Basic {
			[SLSiteBuildingInfoFormLabels.contacts]: SLSiteBuildingInfoContacts[]
		}
		interface BaseForm {
			[SLSiteBuildingInfoFormLabels.buildingName]: string
			[SLSiteBuildingInfoFormLabels.squareFootage]: number | null
			[SLSiteBuildingInfoFormLabels.notes]: string | null
			[SLSiteBuildingInfoFormLabels.buildingTypeId]: number | null
		}
		interface Form extends BaseForm {
			[SLSiteBuildingInfoFormLabels.contacts]: SLSiteBuildingInfoFormContacts[]
		}

		interface Request extends BaseForm {
			[SLSiteBuildingInfoFormLabels.contacts]: {
				id?: number
				contactId: number
				isActive: boolean
			}[]
		}
	}

	namespace Document {
		interface Basic {
			id: number
			documentName: string
			allowCustomerToSee: boolean
		}
		interface Form {
			[SLDocumentsFormLabels.documentName]: string
			[SLDocumentsFormLabels.allowCustomerToSee]: boolean
			[SLDocumentsFormLabels.documentTypeId]: number | null
			[SLDocumentsFormLabels.file]: DefaultFile | null
		}
		interface Response extends Form {
			[SLDocumentsFormLabels.documentType]: Entity
			[SLDocumentsFormLabels.fileName]: string
			id: Entity['id']
		}
		interface Request extends Form {
			[SLDocumentsFormLabels.documentTypeId]: number
			id?: Entity['id']
		}
	}

	namespace Program {
		interface Basic {
			[SLProgramBasicFields.id]: number
			[SLProgramBasicFields.salesforceId]: BasicString
			[SLProgramBasicFields.programName]: BasicString
			[SLProgramBasicFields.programDescription]: BasicString
			[SLProgramBasicFields.isActive]: boolean
		}

		namespace Form {
			interface Generic {
				[SLProgramGenericFormFields.salesforceId]: BasicString
				[SLProgramGenericFormFields.programName]: BasicString
				[SLProgramGenericFormFields.description]: BasicString
				[SLProgramGenericFormFields.isActive]: boolean
			}

			interface Valid extends Generic {
				[SLProgramGenericFormFields.salesforceId]: NonNullable<BasicString>
				[SLProgramGenericFormFields.programName]: NonNullable<BasicString>
				[SLProgramGenericFormFields.description]: NonNullable<BasicString>
			}
		}

		namespace Request {
			interface Generic {
				[SLProgramBasicFields.salesforceId]: NonNullable<BasicString>
				[SLProgramBasicFields.programName]: NonNullable<BasicString>
				[SLProgramBasicFields.programDescription]: NonNullable<BasicString>
				[SLProgramBasicFields.isActive]: boolean
			}
		}
	}

	namespace Contact {
		interface BasicFields {
			email: string
			contactName: string
			id: number
		}
		interface Basic extends BasicFields {
			contactId: number
			officePhone: string
			mobilePhone: string
			isActive: boolean
		}
		interface Request {
			contactId: Key
			isActive: boolean
		}

		namespace Create {
			interface Form extends DefaultContact.Form.Create.Basic, SlContactFlags {}
			interface Request extends DefaultContact.Form.Create.Request, SlContactFlags {}
		}
	}

	namespace Notes {
		interface Basic {
			programManagerInvoiceReviewNotes: string
			supplierInvoiceWithoutCustomerBillNotes: string
			invoiceSendMethodNotes: string
			serviceSiteNotes: string
			specialProjectNotes: string
			backupRequiredNotes: string
			commentsForBpmp: string
			rebateCheckNotes: string
			facilityTypeNotes: string
		}
		interface Form {
			type: string | null
			value: string | null
		}
	}

	namespace Search {
		interface Config {
			[ServiceLocationSearchResultFields.searchResultById]: ConfigItem<ServiceLocationSearchResultFields>
			[ServiceLocationSearchResultFields.searchResultByName]: ConfigItem<ServiceLocationSearchResultFields>
		}

		interface Result {
			[ServiceLocationSearchResultFields.searchResultById]: ServiceLocation.AddressEntity[]
			[ServiceLocationSearchResultFields.searchResultByName]: ServiceLocation.AddressEntity[]
			[ServiceLocationSearchResultFields.searchResultByRefCode]: Entity[]
			[ServiceLocationSearchResultFields.searchResultByCompanyId]: Entity[]
			[ServiceLocationSearchResultFields.searchResultByCompanyName]: Entity[]
			[ServiceLocationSearchResultFields.searchResultBySupplierServiceSiteCode]: Entity[]
		}
		interface AddressResult {
			[SLAddressSearchResultFields.stateResponse]: Entity[]
			[SLAddressSearchResultFields.cityResponse]: Entity['name'][]
			[SLAddressSearchResultFields.addressResponse]: Entity['name'][]
		}
		interface ProgramManagerResult {
			id: Entity['id']
			firstName: string
			lastName: string
			middleInitial: string | null
		}
	}

	namespace CurrentServiceProductState {
		interface Basic {
			[CurrentServiceProductStateFields.id]: number
			[CurrentServiceProductStateFields.name]: BasicString
			[CurrentServiceProductStateFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFields.unit]: Entity<number>
			[CurrentServiceProductStateFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFields.disposalMethod]: Entity<number>
			[CurrentServiceProductStateFields.currentVendor]: BasicString
			[CurrentServiceProductStateFields.contactExpiration]: BasicString
			[CurrentServiceProductStateFields.containerSize]: BasicString
			[CurrentServiceProductStateFields.containerType]: BasicString
		}

		interface Form {
			[CurrentServiceProductStateFormFields.name]: BasicString
			[CurrentServiceProductStateFormFields.currentVendor]: BasicString
			[CurrentServiceProductStateFormFields.containerType]: BasicString
			[CurrentServiceProductStateFormFields.containerSize]: BasicString
			[CurrentServiceProductStateFormFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFormFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFormFields.unitOfMeasureId]: BasicNumber
			[CurrentServiceProductStateFormFields.treatmentMethodId]: BasicNumber
			[CurrentServiceProductStateFormFields.contactExpiration]: Nullable<Moment>
		}

		interface Request {
			[CurrentServiceProductStateFormFields.name]: BasicString
			[CurrentServiceProductStateFormFields.currentVendor]: BasicString
			[CurrentServiceProductStateFormFields.containerType]: BasicString
			[CurrentServiceProductStateFormFields.containerSize]: BasicString
			[CurrentServiceProductStateFormFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFormFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFormFields.unitOfMeasureId]: BasicNumber
			[CurrentServiceProductStateFormFields.treatmentMethodId]: BasicNumber
			[CurrentServiceProductStateFormFields.contactExpiration]: BasicString
		}
	}

	namespace CurrentSupplierContact {
		interface Basic {
			[CurrentSupplierContactFields.id]: number
			[CurrentSupplierContactFields.currentVendor]: BasicString
			[CurrentSupplierContactFields.vendorContactName]: BasicString
			[CurrentSupplierContactFields.vendorContactEmail]: BasicString
			[CurrentSupplierContactFields.vendorContactPhone]: BasicString
			[CurrentSupplierContactFields.wastesMaterialsServices]: BasicString
		}

		interface Form {
			[CurrentSupplierContactFormFields.currentVendor]: string
			[CurrentSupplierContactFormFields.vendorContactName]: string
			[CurrentSupplierContactFormFields.vendorContactEmail]: string
			[CurrentSupplierContactFormFields.vendorContactPhone]: string
			[CurrentSupplierContactFormFields.existingPaymentTerms]: string
			[CurrentSupplierContactFormFields.wastesMaterialsServices]: string
			[CurrentSupplierContactFormFields.isUnderContact]: boolean
		}
	}

	namespace HistoricalInvoice {
		interface Basic {
			[HistoricalInvoiceFields.id]: number
			[HistoricalInvoiceFields.documentName]: BasicString
			[HistoricalInvoiceFields.uploadDate]: BasicString
		}

		interface Form {
			[HistoricalInvoiceFormFields.files]: Nullable<MultipleDefaultFile>
		}

		interface Request {
			Files: File[]
		}
	}
}
